<script setup>
import { onMounted, reactive, ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { notification } from 'ant-design-vue';
import DataTable from '@/components/common/DataTable.vue';
import SearchCard from '@/components/common/SearchCard.vue';
import TableIndexColumn from '@/components/common/TableIndexColumn.vue';
import usePagination from '@/composables/usePagination';
import { usePermission } from '@/composables/usePermission';
import DialogDelete from '@/views/admin/DialogDelete.vue';
import ModalAdd from '@/views/admin/ModalAdd.vue';
import ModalEdit from '@/views/admin/ModalEdit.vue';
import { adminList } from '@/api/admin/adminList';
import { deleteAdmin } from '@/api/admin/deleteAdmin';
import { getRole } from '@/api/roles/getRole';
import { roleList } from '@/api/roles/roleList';
import { assignRoleToManager } from '@/api/assign-role/assignRoleToManager';
import formatDateTime from '@/utils/formatDateTime';

// 使用權限控制
const { availablePermissions } = usePermission('AdminList');

// 讀取路由查詢參數
const route = useRoute();

// 搜尋表單
const searchForm = ref({
    name: '',
    email: '',
    role_id: ''
});

const formItems = ref([
    {
        label: '姓名',
        key: 'name',
        placeholder: '請輸入姓名'
    },
    {
        label: '信箱',
        key: 'email',
        placeholder: '請輸入信箱'
    }
]);

// 角色列表（提前宣告，避免計算屬性閉包解析順序問題）
const roleTable = ref([]);

// 將角色列表轉為下拉選項（以 ID 查詢）
const roleOptions = computed(() => {
    try {
        if (!Array.isArray(roleTable.value)) {
            console.warn('角色列表格式錯誤，期望陣列格式');
            return [];
        }
        return roleTable.value
            .map((r) => {
                if (!r || typeof r !== 'object' || !r.id || !r.name) {
                    console.warn('角色物件格式錯誤:', r);
                    return null;
                }
                return { label: r.name, value: r.id };
            })
            .filter(Boolean);
    } catch (error) {
        console.error('處理角色選項時發生錯誤:', error);
        return [];
    }
});

// 合併原有欄位與角色下拉欄位
const formItemsWithRole = computed(() => {
    try {
        const baseItems = Array.isArray(formItems.value) ? formItems.value : [];
        const roleItem = {
            label: '角色',
            key: 'role_id',
            type: 'select',
            placeholder: '請選擇角色',
            options: roleOptions.value
        };
        return [...baseItems, roleItem];
    } catch (error) {
        console.error('處理表單項目時發生錯誤:', error);
        return formItems.value || [];
    }
});

// 更新搜尋表單處理函數
const handleFormChange = ({ key, value }) => {
    searchForm.value[key] = value;
};

// 重置搜尋
const handleReset = async () => {
    searchForm.value = {
        name: '',
        email: '',
        role_id: ''
    };
    resetPagination();
    await getAdminList(1, pagination.value.pageSize);
};

// 表格資料
const tableData = ref([]);

// 權限資料
const rolePermissions = ref([]);

// 當前選擇的權限資料
let currentRoles = ref(new Set());

// 當前看的管理員權限資料
let currentPermissionLoading = ref(null);

// 當前選擇管理者的角色權限
const currentID = ref(null);

// 當前操作的行資料
const rowData = ref({});

// 分頁設置
const { pagination, updatePagination, resetPagination } = usePagination({
    pageSize: 20
});

// 表格密度設置
const tableSize = ref('middle');

const handleSizeChange = (size) => {
    tableSize.value = size;
};

// 表格欄位定義（根據權限動態顯示）
const columns = computed(() => {
    const baseColumns = [
        {
            title: '序號',
            key: 'index',
            width: 40,
            fixed: 'left'
        },
        {
            title: '姓名',
            key: 'name',
            dataIndex: 'name',
            width: 120
        },
        {
            title: '信箱',
            key: 'email',
            dataIndex: 'email',
            width: 200
        },
        {
            title: '角色',
            key: 'roles',
            dataIndex: 'roles',
            width: 120
        },
        {
            title: '權限',
            key: 'permissions',
            dataIndex: 'permissions',
            width: 120
        },
        {
            title: '建立時間',
            key: 'created_at',
            dataIndex: 'created_at',
            width: 180
        },
        {
            title: '更新時間',
            key: 'updated_at',
            dataIndex: 'updated_at',
            width: 180
        }
    ];

    // 只有在有任何操作權限時才添加操作欄位
    if (
        availablePermissions.value.update ||
        availablePermissions.value.delete ||
        availablePermissions.value.assignRole
    ) {
        baseColumns.push({
            title: '操作',
            key: 'action',
            width: 80,
            fixed: 'right'
        });
    }

    return baseColumns;
});

// 獲取角色列表
const getRoleList = async (page = 1, pageSize = 20) => {
    try {
        const response = await roleList(pageSize, page);
        console.log('角色列表 API 回應:', response);

        if (response.status === 1) {
            roleTable.value = response.data || [];
        } else {
            notification.error({
                message: '獲取角色列表失敗',
                description: response.message || '請稍後再試'
            });
        }
    } catch (error) {
        console.error('獲取角色列表 API 錯誤:', error);
        notification.error({
            message: '獲取角色列表失敗',
            description: '網路連線錯誤，請稍後再試'
        });
    }
};

// 獲取管理者列表
const getAdminList = async (page = 1, pageSize = 20) => {
    try {
        const response = await adminList(pageSize, page, searchForm.value);
        console.log('管理者列表 API 回應:', response);

        if (response.status === 1) {
            // API 已統一為格式 A：data 直接是陣列
            tableData.value = Array.isArray(response.data) ? response.data : [];
            updatePagination({
                current: page,
                pageSize,
                total: response.total || 0
            });
        } else {
            notification.error({
                message: '獲取管理者列表失敗',
                description: response.message || '請稍後再試'
            });
        }
    } catch (error) {
        console.error('獲取管理者列表 API 錯誤:', error);
        notification.error({
            message: '獲取管理者列表失敗',
            description: '網路連線錯誤，請稍後再試'
        });
    }
};

// 搜尋處理
const handleSearch = async () => {
    await getAdminList(1, pagination.value.pageSize);
};

// 表格變化處理（DataTable 外部分頁：emit { pagination, filters, sorter }）
const handleTableChange = ({ pagination: pag }) => {
    if (!pag?.current) return;
    updatePagination(pag);
    getAdminList(pag.current, pag.pageSize);
};

// 刷新處理
const handleRefresh = async () => {
    await getAdminList(pagination.value.current, pagination.value.pageSize);
};

// 彈窗狀態管理
const showDialog = reactive({
    add: false,
    delete: false,
    permission: false,
    roles: false
});

// 當前操作的行資料
const deleteId = ref(null);
const deleteName = ref('');

// 刪除確認處理
const handleDeleteConfirm = async (isConfirm) => {
    if (isConfirm) {
        try {
            const response = await deleteAdmin(deleteId.value);

            if (response.status === 1) {
                notification.success({
                    message: '刪除成功',
                    description: '管理者資料已成功刪除'
                });

                showDialog.delete = false;
                await getAdminList(1, pagination.value.pageSize);
            } else {
                notification.error({
                    message: '刪除失敗',
                    description: response.message || '請稍後再試'
                });
            }
        } catch (error) {
            console.error('刪除 API 錯誤:', error);
            notification.error({
                message: '刪除失敗',
                description: '網路連線錯誤，請稍後再試'
            });
        }
    } else {
        console.log('取消刪除操作');
    }
};

// 新增角色
const handleAdd = () => {
    showDialog.add = true;
};

// 編輯
const handleEditAction = (record) => {
    showDialog.edit = true;
    rowData.value = JSON.parse(JSON.stringify(record));
};

// 刪除
const handleDelAction = (record) => {
    showDialog.delete = true;
    deleteId.value = record.id;
    deleteName.value = record.name;
};

const handleRoleAction = (id, record) => {
    showDialog.roles = true;

    const arr = [];

    record.roles.forEach((p) => {
        arr.push(p.id);
    });

    currentID.value = id;
    currentRoles.value = new Set(arr);
};

const viewRolePermissions = (id, roles) => {
    const roleList = Array.isArray(roles) ? roles : [];
    currentPermissionLoading.value = id;

    if (roleList.length === 0) {
        rolePermissions.value = [];
        showDialog.permission = true;
        currentPermissionLoading.value = null;
        return;
    }

    Promise.all(roleList.map((role) => getRole(role.id)))
        .then((vals) => {
            showDialog.permission = true;
            rolePermissions.value = vals
                .map((val) => val?.data)
                .filter(Boolean);
        })
        .catch((err) => {
            console.error('獲取角色權限列表 API 錯誤:', err);
            notification.error({
                message: '獲取角色權限列表失敗',
                description: '網路連線錯誤，請稍後再試'
            });
        })
        .finally(() => {
            currentPermissionLoading.value = null;
        });
};

// 更新管理者角色
const updateManagerRoles = async () => {
    try {
        const response = await assignRoleToManager(currentID.value, [
            ...currentRoles.value
        ]);

        if (response.status === 1) {
            notification.success({
                message: '更新成功',
                description: '管理者角色資料已成功更新'
            });

            showDialog.roles = false;
            await getAdminList(1, pagination.value.pageSize);
        } else {
            notification.error({
                message: '更新失敗',
                description: response.message || '請稍後再試'
            });
        }
    } catch (error) {
        console.error('更新 API 錯誤:', error);
        notification.error({
            message: '更新失敗',
            description: '網路連線錯誤，請稍後再試'
        });
    } finally {
        currentID.value = null;
    }
};

// 選擇管理者角色
const toggleRoles = (id) => {
    currentRoles.value.has(id)
        ? currentRoles.value.delete(id)
        : currentRoles.value.add(id);
};

const permissionOk = () => {
    showDialog.permission = false;
};

/**
 * 去掉頂層模組名後顯示完整路徑。
 * 舊版用 split('.')[1]/[2] 會把「諮詢工作台.版本.檢視」顯示成只剩「版本」。
 */
const PERMISSION_LEAF_ALIASES = {
    參數設置: '參數設置（簡版／可約區間）',
    開班表: '開班表（可排班日）',
    預約規則: '完整參數設置（工程師）'
};

const permissionLeafLabel = (fullName) => {
    if (fullName == null || fullName === '') return '';
    const parts = String(fullName).split('.');
    if (parts.length < 2) return String(fullName);
    const leaf = parts.slice(1).join('．');
    return PERMISSION_LEAF_ALIASES[leaf] || leaf;
};

// 新增成功處理
const handleAddSuccess = async (data) => {
    showDialog.add = false;
    await getAdminList(1, pagination.value.pageSize);
};

// 編輯成功處理
const handleEditSuccess = async (data) => {
    showDialog.edit = false;
    notification.success({
        message: '編輯成功',
        description: '管理員資料已成功更新'
    });
    await getAdminList(1, pagination.value.pageSize);
};

onMounted(() => {
    // 若帶有 ?role_id= 參數則預填搜尋並觸發查詢
    const initRoleId = route?.query?.role_id;
    if (initRoleId) {
        // 轉數字以利 a-select 嚴格等於比對 option.value（為 id 整數）
        const numericId = Number(initRoleId);
        searchForm.value.role_id = Number.isNaN(numericId)
            ? String(initRoleId)
            : numericId;
    }
    getAdminList(1, 20);
    getRoleList(1, 0);
});
</script>

<template>
    <a-modal
        :width="520"
        v-model:open="showDialog.roles"
        title="編輯管理者角色"
        wrap-class-name="edit-modal"
        okText="儲存"
        cancelText="取消"
        centered
        @ok="updateManagerRoles"
    >
        <p class="mb-3 text-sm text-[#565962]">點選標籤以複選角色。</p>
        <div class="flex flex-wrap gap-2">
            <a-tag
                v-for="role in roleTable"
                :key="role.id"
                :color="currentRoles.has(role.id) ? 'processing' : 'default'"
                class="m-0 cursor-pointer px-3 py-1 text-sm"
                @click="toggleRoles(role.id)"
            >
                {{ role.name }}
            </a-tag>
        </div>
    </a-modal>

    <a-modal
        v-model:open="showDialog.permission"
        :width="960"
        title="查看角色權限"
        wrap-class-name="edit-modal"
        :footer="null"
        centered
        @ok="permissionOk"
    >
        <div
            class="max-h-[72vh] space-y-6 overflow-y-auto pr-1 [-ms-overflow-style:none] [scrollbar-width:thin]"
        >
            <div
                v-if="!rolePermissions.length"
                class="permission-empty"
            >
                <a-empty
                    description="尚未指派角色或此角色沒有權限"
                >
                    <template #image>
                        <div class="permission-empty__icon" aria-hidden="true">
                            —
                        </div>
                    </template>
                </a-empty>
                <p class="permission-empty__hint">
                    可先到「角色」為管理者指派角色，或到角色管理設定權限。
                </p>
            </div>

            <section
                v-for="(roleBlock, rIdx) in rolePermissions"
                :key="roleBlock.id ?? `role-${rIdx}`"
                class="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm"
            >
                <div class="mb-4 flex flex-wrap items-center gap-2 border-b border-slate-100 pb-3">
                    <span class="text-xs font-medium uppercase tracking-wide text-[#919191]">角色</span>
                    <a-tag color="processing" class="m-0 text-sm font-semibold">
                        {{ roleBlock.name }}
                    </a-tag>
                </div>

                <div
                    v-if="!(roleBlock.permissions || []).length"
                    class="permission-empty permission-empty--compact"
                >
                    <p class="permission-empty__text">此角色尚未設定任何權限</p>
                </div>

                <div
                    v-else
                    class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                >
                    <div
                        v-for="(mod, mIdx) in roleBlock.permissions || []"
                        :key="mod.name ?? `mod-${mIdx}`"
                        class="flex min-h-[120px] flex-col rounded-lg border border-slate-200/80 bg-[#fafcfb] p-3"
                    >
                        <div
                            class="mb-2 border-b border-slate-200/80 pb-2 text-sm font-semibold text-[#272b36]"
                        >
                            {{ mod.name }}
                        </div>
                        <ul class="m-0 flex-1 list-none space-y-2 p-0">
                            <li
                                v-for="(p, pIdx) in mod.data || []"
                                :key="p.id ?? p.name ?? `p-${pIdx}`"
                            >
                                <div class="text-sm font-medium text-[#565962]">
                                    {{ permissionLeafLabel(p.name) }}
                                </div>
                                <ul
                                    v-if="p?.data?.length"
                                    class="mt-1.5 space-y-1 border-l-2 border-[rgba(102,209,171,0.35)] pl-2.5"
                                >
                                    <li
                                        v-for="(c, cIdx) in p.data"
                                        :key="c.id ?? c.name ?? `c-${cIdx}`"
                                        class="text-xs leading-relaxed text-[#565962]"
                                    >
                                        {{ permissionLeafLabel(c.name) }}
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    </a-modal>

    <ModalAdd
        :open="showDialog.add"
        @update:open="showDialog.add = $event"
        @success="handleAddSuccess"
    />

    <ModalEdit
        :open="showDialog.edit"
        :manager-data="rowData"
        centered
        @update:open="showDialog.edit = $event"
        @success="handleEditSuccess"
    />

    <DialogDelete
        v-model:open="showDialog.delete"
        :admin-name="deleteName"
        @confirm="handleDeleteConfirm"
    />

    <SearchCard
        :form-items="formItemsWithRole"
        :form-data="searchForm"
        @change="handleFormChange"
        @search="handleSearch"
        @reset="handleReset"
    />

    <DataTable
        title="列表"
        :columns="columns"
        :data-source="tableData"
        :pagination="pagination"
        :scroll="{ x: 1200 }"
        @refresh="handleRefresh"
        @change="handleTableChange"
    >
        <template #titleExtra>
            <a-button
                v-if="availablePermissions.create"
                type="primary"
                @click="handleAdd"
            >
                新增
            </a-button>
        </template>
        <template #bodyCell="{ column, record, index }">
            <template v-if="column.key === 'index'">
                <TableIndexColumn :index="index" :pagination="pagination" />
            </template>

            <template v-else-if="column.key === 'email'">
                {{ record.email ?? '-' }}
            </template>

            <template v-else-if="column.key === 'roles'">
                <a-tag
                    class="m-1"
                    v-for="role in record.roles"
                    :key="`${record.id + role.id}`"
                    color="purple"
                >
                    {{ role.name }}
                </a-tag>
            </template>

            <template v-else-if="column.key === 'permissions'">
                <a-button
                    size="small"
                    type="primary"
                    :loading="record.id === currentPermissionLoading"
                    @click="viewRolePermissions(record.id, record.roles)"
                >
                    查看
                </a-button>
            </template>

            <template v-else-if="column.key === 'created_at'">
                {{ formatDateTime(record.created_at) }}
            </template>

            <template v-else-if="column.key === 'updated_at'">
                {{ formatDateTime(record.updated_at) }}
            </template>

            <template v-else-if="column.key === 'status'">
                <a-tag
                    :color="record.status === 'active' ? 'success' : 'error'"
                >
                    {{ record.status === 'active' ? '啟用' : '停用' }}
                </a-tag>
            </template>

            <template v-else-if="column.key === 'action'">
                <template
                    v-if="
                        availablePermissions.update ||
                        availablePermissions.delete ||
                        availablePermissions.assignRole
                    "
                >
                    <div class="admin-table-actions">
                        <a-button
                            v-if="availablePermissions.update"
                            size="small"
                            class="admin-table-actions__btn"
                            @click="handleEditAction(record)"
                        >
                            編輯
                        </a-button>
                        <a-button
                            v-if="availablePermissions.assignRole"
                            size="small"
                            class="admin-table-actions__btn"
                            @click="handleRoleAction(record.id, record)"
                        >
                            角色
                        </a-button>
                        <a-button
                            v-if="availablePermissions.delete"
                            size="small"
                            danger
                            class="admin-table-actions__btn"
                            @click="handleDelAction(record)"
                        >
                            刪除
                        </a-button>
                    </div>
                </template>
                <template v-else>
                    <a-tag color="default">僅查看</a-tag>
                </template>
            </template>
        </template>
    </DataTable>
</template>

<style scoped>
.permission-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 220px;
    padding: 28px 16px;
    border-radius: 12px;
    border: 1px dashed #e2e8f0;
    background: #f8fafc;
    text-align: center;
}

.permission-empty--compact {
    min-height: 88px;
    padding: 18px 12px;
}

.permission-empty__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    margin: 0 auto 8px;
    border-radius: 999px;
    background: #eef2f7;
    color: #94a3b8;
    font-size: 20px;
    font-weight: 600;
    line-height: 1;
}

.permission-empty__hint,
.permission-empty__text {
    margin: 0;
    max-width: 28rem;
    font-size: 13px;
    line-height: 1.5;
    color: #64748b;
}

.permission-empty :deep(.ant-empty-description) {
    color: #475569;
    font-size: 14px;
}
</style>
